import usePrivateAxios from "@/common/api/usePrivateAxios";
import { useMutation } from "@tanstack/react-query";
import { UpdateEstablishmentCompanyValidationSchema } from "../schemas/updateEstablishmentCompany.schema";
import { AxiosResponse } from "axios";
import { getRailsUpdate } from "@/common/utils/getRailsUpdate";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { mapToDeletion } from "@/common/utils/railsHelpers";
import { unFormatPercentage } from "@/common/utils/unFormatters";
import { ErrorResponse } from "@/common/types/errorResponse";

interface UseUpdateEstablishmentCompanyProps {
  establishmentCompanyId?: string,
  establishmentCompany?: EstablishmentCompanyData,
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: ErrorResponse) => void,
}

export const useUpdateEstablishmentCompany = ({ establishmentCompanyId, establishmentCompany, onSuccess, onError }: UseUpdateEstablishmentCompanyProps) => {
  const privateAxios = usePrivateAxios();

  return useMutation({
    mutationKey: ['updateEstablishmentCompany', establishmentCompanyId],
    mutationFn: async (form: UpdateEstablishmentCompanyValidationSchema) => {
      const payload = {
        company_product_categories_attributes: getRailsUpdate(
          establishmentCompany?.company_product_categories || [],
          form.product_categories || [],
          (x) => x.product_category.id,
          (x) => x.value,
          mapToDeletion,
          (item: { value: string; }) => ({ product_category_id: item.value }),
        ),
        commercial_manager_id: form.commercial_manager?.value,
        commercial_manager_substitute_id: form.commercial_manager_substitute?.value || 'null',
        observations: form.observations,
        matrix_branch_attributes: {
          payment_advance_daily_interest: form.matrix_branch_attributes?.payment_advance_daily_interest
            ? unFormatPercentage(form.matrix_branch_attributes?.payment_advance_daily_interest)
            : undefined,
        },
        time_zone_id: form.time_zone_id?.value,
        tow_service_available: form.tow_service_available,
        ca_module_enabled: form.ca_module_enabled,
        cm_module_enabled: form.cm_module_enabled,
        tmais_module_enabled: form.tmais_module_enabled,
      };

      return privateAxios.putForm(`/establishment/companies/${establishmentCompanyId}`, { company: payload });
    },
    onSuccess,
    onError,
  });
};
